<template>
  <div class="login-wrapper">
    <link
      rel="stylesheet"
      href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0"
    />
    <div class="login-card">
      <!-- Panel izquierdo -->
      <div class="login-left">
        <img
          src="../../assets/images/login.png"
          class="login-image"
          alt="Ilustración de inicio de sesión"
        />
        <div class="login-left-text">
          <h2>Bienvenido</h2>
          <p>
            Recuerda cambiar tu contraseña regularmente y nunca compartirla.
            Tu seguridad es nuestra prioridad.
          </p>
        </div>
      </div>

      <!-- Panel derecho -->
      <div class="login-right">

        <!-- ── PASO 1: Credenciales ───────────────────── -->
        <template v-if="!requiereMfa">
          <div class="login-header">
            <h4>Inicio de sesión</h4>
          </div>
          <div class="form-group" :class="{ 'has-error': errors.usuario }">
            <label for="usuario">Correo electrónico</label>
            <input
              id="usuario"
              type="email"
              class="form-control form-control-lg"
              placeholder="ejemplo@correo.com"
              v-model.trim="usuario.usuario"
              @keyup.enter="login"
              autocomplete="email"
            />
            <span v-if="errors.usuario" class="field-error">{{ errors.usuario }}</span>
          </div>
          <div class="form-group" :class="{ 'has-error': errors.clave }">
            <label for="clave">Contraseña</label>
            <div class="input-group">
              <input
                :type="passwordVisible ? 'text' : 'password'"
                id="clave"
                class="form-control form-control-lg"
                placeholder="Ingresa tu contraseña"
                v-model="usuario.clave"
                @keyup.enter="login"
                autocomplete="current-password"
              />
              <button
                class="btn-toggle-password"
                type="button"
                :aria-label="passwordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                @click="togglePasswordVisibility"
              >
                <span class="material-symbols-outlined">
                  {{ passwordVisible ? "visibility" : "visibility_off" }}
                </span>
              </button>
            </div>
            <span v-if="errors.clave" class="field-error">{{ errors.clave }}</span>
          </div>
          <div class="form-extras">
            <label class="form-check-label">
              <input type="checkbox" v-model="rememberMe" class="form-check-input" />
              Recuérdame
            </label>
            <router-link to="/OlvidoClave" class="link-forgot">
              ¿Olvidó su contraseña?
            </router-link>
          </div>
          <div v-if="errors.general" class="alert-error" role="alert">
            {{ errors.general }}
          </div>
          <button
            type="button"
            class="btn-login"
            :disabled="isLoading"
            @click="login"
          >
            <span v-if="isLoading" class="spinner" aria-hidden="true"></span>
            {{ isLoading ? "Iniciando sesión..." : "Iniciar sesión" }}
          </button>
        </template>

        <!-- ── PASO 2: Código OTP ─────────────────────── -->
        <template v-else>
          <div class="login-header">
            <h4>Verificación en dos pasos</h4>
          </div>
          <div class="mfa-info">
            <span class="material-symbols-outlined mfa-icon">smartphone</span>
            <p>Ingresa el código de 6 dígitos de tu aplicación autenticadora.</p>
          </div>
          <div class="form-group" :class="{ 'has-error': errors.otp }">
            <label for="otp">Código de verificación</label>
            <input
              id="otp"
              type="text"
              class="form-control form-control-lg otp-input"
              placeholder="000000"
              v-model.trim="otpCodigo"
              @keyup.enter="verificarOtp"
              maxlength="6"
              autocomplete="one-time-code"
              inputmode="numeric"
            />
            <span v-if="errors.otp" class="field-error">{{ errors.otp }}</span>
          </div>
          <div v-if="errors.general" class="alert-error" role="alert">
            {{ errors.general }}
          </div>
          <button
            type="button"
            class="btn-login"
            :disabled="isLoading"
            @click="verificarOtp"
          >
            <span v-if="isLoading" class="spinner" aria-hidden="true"></span>
            {{ isLoading ? "Verificando..." : "Verificar código" }}
          </button>
          <button
            type="button"
            class="btn-back"
            :disabled="isLoading"
            @click="volverAlLogin"
          >
            <span class="material-symbols-outlined" style="font-size:16px">arrow_back</span>
            Volver al inicio de sesión
          </button>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import swal from "sweetalert2";

const SESSION_KEYS = [
  "token", "idUsuario", "idPersona", "idMedico", "idPerfil",
  "idSuscripcion", "idPlan", "idPrestador", "bienvenida", "suscriptor",
  "rol", "idEmpresa", "nombreEmpresa", "nombrePersona",
  "moduloAdministracion", "moduloAtencion", "moduloAuditoria",
  "moduloFacturacion", "moduloDashboards",
  "menuParametros", "menuProcesos", "menuReportes", "menuAyuda",
  "opcionConsulta", "opcionOperaciones", "opcionDescarga", "opcionMenu",
];

export default {
  data() {
    return {
      // Paso 1
      usuario:         { usuario: "", clave: "" },
      sesion:          {},
      errors:          {},
      isLoading:       false,
      passwordVisible: false,
      rememberMe:      false,
      // Paso 2 — MFA
      requiereMfa:     false,
      otpCodigo:       "",
      preAuthToken:    null,
    };
  },
  mounted() {
    this.checkRememberMe();
  },
  methods: {

    // ── Validaciones ────────────────────────────────────
    validate() {
      this.errors = {};
      if (!this.usuario.usuario) {
        this.errors.usuario = "Ingrese su correo electrónico.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.usuario.usuario)) {
        this.errors.usuario = "Ingrese un correo electrónico válido.";
      }
      if (!this.usuario.clave) {
        this.errors.clave = "Ingrese la contraseña.";
      }
      return Object.keys(this.errors).length === 0;
    },

    validateOtp() {
      this.errors = {};
      if (!this.otpCodigo) {
        this.errors.otp = "Ingrese el código de verificación.";
      } else if (!/^\d{6}$/.test(this.otpCodigo)) {
        this.errors.otp = "El código debe tener exactamente 6 dígitos.";
      }
      return Object.keys(this.errors).length === 0;
    },

async login() {
  if (!this.validate() || this.isLoading) return;
  this.isLoading = true;
  this.errors    = {};
  try {
    const resp = await api.post("/login", this.usuario);
    
    // ── Agrega esto para ver qué devuelve el backend ──
    console.log("resp.data completo:", JSON.stringify(resp.data));
    console.log("resp.data.requiereMfa:", resp.data.requiereMfa);
    console.log("resp.data.result:", resp.data.result);

    if (resp.data.requiereMfa) {
      console.log("→ Mostrando pantalla OTP");
      this.preAuthToken = resp.data.preAuthToken;
      this.requiereMfa  = true;
      return;
    }

    const result = resp.data.result;
    console.log("→ Flujo normal, result:", result);
    await this.completarSesion(result);

  } catch (error) {
    this.handleLoginError(error);
  } finally {
    this.isLoading = false;
  }
},

async verificarOtp() {
  if (!this.validateOtp() || this.isLoading) return;
  this.isLoading = true;
  this.errors    = {};
  try {
    const resp = await api.post(
      "/auth/verify-otp",
      { codigo: this.otpCodigo },
      { headers: { Authorization: `Bearer ${this.preAuthToken}` } }
    );

    // verify-otp retorna TokenDto directamente (sin envoltura result)
    await this.completarSesion(resp.data);

    } catch (error) {
        const msg = error.response?.data?.message;
        if (error.response?.status === 401 && msg) {
            // Token expirado → volver al login con mensaje
            this.errors.general = msg;
            this.volverAlLogin();
        } else if (error.response?.status === 401) {
            this.errors.otp = "Código incorrecto. Intenta de nuevo.";
            this.otpCodigo  = "";
        } else {
            this.errors.general = "Error al verificar el código. Intenta de nuevo.";
        }
    } finally {
        this.isLoading = false;
    }
},

async completarSesion(result) {
  console.log("result.nombre:", result.nombrePersona);
  console.log("result.idUsuario:", result.idUsuario);

  this.saveSession(result);

  const tiempoResp = await api.get(
    `/suscripcions/${result.idSuscripcion}/tiempo-restante`
  );
  if (this.isSubscriptionExpired(tiempoResp.data)) {
    const confirm = await swal.fire({
      title:             "Suscripción finalizada",
      text:              "Tu tiempo de suscripción ha finalizado. Renueva tu plan para continuar.",
      icon:              "error",
      confirmButtonText: "Renovar ahora",
      showCancelButton:  true,
      cancelButtonText:  "Cancelar",
      confirmButtonColor:"#3085d6",
      cancelButtonColor: "#d33",
    });
    if (confirm.value) this.$router.push("/Renovacion01PLan");
    else swal.fire("Información", "Contacta a soporte si necesitas ayuda.", "info");
    return;
  }

  await Promise.allSettled([
    this.registrarSesion(),
    this.cargarNombreCompleto(),
  ]);

  if (this.rememberMe) this.setRememberMe();
  this.$router.push("/MenuPrincipal");
},

    volverAlLogin() {
      this.requiereMfa  = false;
      this.preAuthToken = null;
      this.otpCodigo    = "";
      this.errors       = {};
    },

    // ── Métodos existentes sin cambios ──────────────────
    saveSession(result) {
      SESSION_KEYS.forEach((key) => {
        if (result[key] !== undefined) {
          window.sessionStorage.setItem(key, result[key]);
        }
      });
      window.sessionStorage.setItem("usuario", result.nombre);
    },

    isSubscriptionExpired(tiempoData) {
      return (
        tiempoData?.dias    === 0 &&
        tiempoData?.horas   === 0 &&
        tiempoData?.minutos === 0 &&
        tiempoData?.segundos === 0
      );
    },

    async registrarSesion() {
      this.sesion = {
        idUsuario:   window.sessionStorage.getItem("idUsuario"),
        fechaReg:    new Date(),
        direccionIp: "",
        userAgent:   navigator.userAgent,
      };
      await api.post("/sesions", this.sesion);
    },

    async cargarNombreCompleto() {
      const idPersona = window.sessionStorage.getItem("idPersona");
      const response  = await api.get(`/vwpersonas/${idPersona}`);
      window.sessionStorage.setItem("nombreCompleto", response.data.nombreCompleto);
    },

    handleLoginError(error) {
      if (!error.response) {
        this.errors.general = "Error al conectar con el servidor. Por favor, intenta de nuevo.";
        return;
      }
      const { status, data } = error.response;
      if (
        status === 401 &&
        (data.message === "Contraseña expirada" || data.message === "Primer acceso")
      ) {
        const isPrimerAcceso = data.message === "Primer acceso";
        swal.fire({
          title:             isPrimerAcceso ? "Primer acceso al sistema" : "Contraseña expirada",
          text:              isPrimerAcceso
            ? "Bienvenido. Por favor cambie su contraseña para continuar."
            : "Tu contraseña ha expirado. Por favor, cámbiala para continuar.",
          icon:              "warning",
          confirmButtonText: "Cambiar contraseña",
          showCancelButton:  true,
          cancelButtonText:  "Cancelar",
          confirmButtonColor:"#3085d6",
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.value) {
            this.$router.push(`/login-actualizar-clave/${data.idUsuario}`);
          }
        });
      } else {
        swal.fire("Error", data.message || "Error al iniciar sesión", "error");
      }
    },

    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },

    setRememberMe() {
      localStorage.setItem(
        "rememberMe",
        JSON.stringify({ username: this.usuario.usuario })
      );
    },

    checkRememberMe() {
      const rememberedData = localStorage.getItem("rememberMe");
      if (rememberedData) {
        try {
          const { username } = JSON.parse(rememberedData);
          this.usuario.usuario = username || "";
          this.rememberMe      = true;
        } catch {
          localStorage.removeItem("rememberMe");
        }
      }
    },
  },
};
</script>

<style scoped>
/* ── Estilos existentes sin cambios ── */
.login-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f4f6f9;
  padding: 2rem;
}
.login-card {
  display: flex;
  width: 100%;
  max-width: 900px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.login-left {
  flex: 1;
  background: linear-gradient(160deg, #003d99 0%, #0059cc 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1.5rem;
}
.login-image {
  width: 100%;
  max-width: 280px;
  border-radius: 12px;
  object-fit: cover;
}
.login-left-text { color: #ffffff; text-align: center; }
.login-left-text h2 { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem; }
.login-left-text p  { font-size: 0.9rem; line-height: 1.6; opacity: 0.85; }
.login-right {
  flex: 1;
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.25rem;
}
.login-header h4 { font-size: 1.4rem; font-weight: 700; color: #003d99; margin: 0; }
.form-group { display: flex; flex-direction: column; gap: 0.4rem; }
.form-group label { font-size: 0.85rem; font-weight: 600; color: #444; }
.form-group .form-control { border-radius: 8px; border: 1.5px solid #dde2ea; transition: border-color 0.2s; }
.form-group .form-control:focus { border-color: #0059cc; box-shadow: 0 0 0 3px rgba(0, 89, 204, 0.12); }
.form-group.has-error .form-control { border-color: #dc3545; }
.field-error { font-size: 0.8rem; color: #dc3545; font-weight: 500; }
.input-group { display: flex; }
.input-group .form-control { border-right: none; border-radius: 8px 0 0 8px; }
.btn-toggle-password {
  display: flex; align-items: center; justify-content: center;
  padding: 0 0.85rem; background: #f8f9fa;
  border: 1.5px solid #dde2ea; border-left: none;
  border-radius: 0 8px 8px 0; cursor: pointer;
  color: #6c757d; transition: background 0.2s, color 0.2s;
}
.btn-toggle-password:hover { background: #e9ecef; color: #003d99; }
.form-extras { display: flex; align-items: center; justify-content: space-between; }
.form-check-label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; cursor: pointer; color: #555; }
.link-forgot { font-size: 0.85rem; color: #dc3545; text-decoration: none; }
.link-forgot:hover { text-decoration: underline; }
.alert-error {
  background: #fff0f0; border: 1px solid #f5c6cb;
  border-radius: 8px; padding: 0.7rem 1rem;
  color: #721c24; font-size: 0.88rem; font-weight: 500;
}
.btn-login {
  display: flex; align-items: center; justify-content: center;
  gap: 0.5rem; width: 100%; padding: 0.85rem;
  background: #003d99; color: #ffffff;
  font-size: 1rem; font-weight: 600;
  border: none; border-radius: 8px; cursor: pointer;
  transition: background 0.2s, transform 0.1s; margin-top: 0.5rem;
}
.btn-login:hover:not(:disabled) { background: #0059cc; }
.btn-login:active:not(:disabled) { transform: scale(0.98); }
.btn-login:disabled { opacity: 0.7; cursor: not-allowed; }

/* ── Estilos nuevos para MFA ── */
.mfa-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #f0f4ff;
  border: 1px solid #c7d7f9;
  border-radius: 10px;
  padding: 1rem;
  color: #003d99;
}
.mfa-icon { font-size: 2rem; flex-shrink: 0; }
.mfa-info p { margin: 0; font-size: 0.9rem; line-height: 1.5; }
.otp-input {
  font-size: 1.5rem !important;
  letter-spacing: 0.5rem;
  text-align: center;
  font-weight: 600;
}
.btn-back {
  display: flex; align-items: center; justify-content: center;
  gap: 0.4rem; width: 100%; padding: 0.7rem;
  background: transparent; color: #6c757d;
  font-size: 0.9rem; font-weight: 500;
  border: 1.5px solid #dde2ea; border-radius: 8px;
  cursor: pointer; transition: all 0.2s;
}
.btn-back:hover:not(:disabled) { background: #f8f9fa; color: #003d99; border-color: #003d99; }
.btn-back:disabled { opacity: 0.6; cursor: not-allowed; }

.spinner {
  width: 16px; height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff; border-radius: 50%;
  animation: spin 0.7s linear infinite; flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 640px) {
  .login-card { flex-direction: column; }
  .login-left { padding: 2rem 1.5rem; }
  .login-right { padding: 2rem 1.5rem; }
}
</style>